import { useState } from "react";
import type { Integration } from "../../../../types/integration";
import { getCsrfHeader } from "../../../../lib/csrf";
import styles from "./TenantAuthConfigControl.module.css";

type Props = {
  readonly tenantId: string;
  readonly integrations: ReadonlyArray<Integration>;
};

type Result = { kind: "ok" | "err"; text: string };

// IP-422 admin escape hatch: register a customer's own OAuth app auth config
// (an `ac_...` created in the Composio dashboard) for THIS tenant + one
// integration — the concierge path when the customer shouldn't drive the
// self-serve modal themselves. The normal path is the customer-facing
// credentials modal on the integration detail page; this control exists for
// staged rollouts and rescues.
export const TenantAuthConfigControl = ({ tenantId, integrations }: Props): JSX.Element => {
  const oauthIntegrations = integrations.filter((i) => i.authType === "oauth2");
  const [slug, setSlug] = useState<string>("");
  const [acId, setAcId] = useState<string>("");
  const [current, setCurrent] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [result, setResult] = useState<Result | null>(null);

  const loadStatus = async (nextSlug: string): Promise<void> => {
    setCurrent(null);
    setResult(null);
    if (!nextSlug) return;
    try {
      const res = await fetch(
        `/api/admin/integration-catalog/${encodeURIComponent(nextSlug)}/composio-auth-config?tenantId=${encodeURIComponent(tenantId)}`,
        { credentials: "include" },
      );
      if (res.status === 404) {
        setResult({ kind: "err", text: "Not a Composio integration — no auth config to manage." });
        return;
      }
      if (!res.ok) throw new Error(`status fetch failed (${res.status})`);
      const body = (await res.json()) as { authConfigId?: string | null };
      setCurrent(body.authConfigId ?? null);
    } catch (err) {
      setResult({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    }
  };

  const save = async (): Promise<void> => {
    if (!slug || acId.trim().length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(
        `/api/admin/integration-catalog/${encodeURIComponent(slug)}/composio-auth-config`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ authConfigId: acId.trim(), tenantId }),
        },
      );
      const body = (await res.json().catch(() => null)) as
        | { authConfigId?: string; message?: string }
        | null;
      if (!res.ok) {
        // 422 carries the gateway's validation verdict verbatim.
        setResult({ kind: "err", text: body?.message ?? `Register failed (${res.status})` });
        return;
      }
      setCurrent(body?.authConfigId ?? acId.trim());
      setAcId("");
      setResult({ kind: "ok", text: "Registered — this tenant now connects through their own app." });
    } catch (err) {
      setResult({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.control}>
      <div className={styles.headerRow}>
        <span className={styles.title}>Customer-owned OAuth app</span>
        <span className={styles.hint}>
          Register an ac_ auth config from the Composio dashboard for one integration on this tenant.
        </span>
      </div>
      <div className={styles.row}>
        <select
          className={styles.select}
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            void loadStatus(e.target.value);
          }}
          disabled={busy}
        >
          <option value="">Select integration…</option>
          {oauthIntegrations.map((i) => (
            <option key={i.slug} value={i.slug}>
              {i.title}
            </option>
          ))}
        </select>
        <input
          className={styles.input}
          type="text"
          placeholder="ac_…"
          value={acId}
          onChange={(e) => setAcId(e.target.value)}
          disabled={busy || slug === ""}
          spellCheck={false}
          autoComplete="off"
        />
        <button
          type="button"
          className={styles.saveBtn}
          onClick={() => void save()}
          disabled={busy || slug === "" || acId.trim().length === 0}
        >
          {busy ? "Saving…" : "Register"}
        </button>
      </div>
      {slug !== "" && result === null ? (
        <span className={styles.status}>
          {current !== null ? `Registered: ${current}` : "No tenant config registered yet."}
        </span>
      ) : null}
      {result !== null ? (
        <span className={result.kind === "ok" ? styles.ok : styles.err}>{result.text}</span>
      ) : null}
    </div>
  );
};
